import { createAsyncThunk, createSlice, isAnyOf, PayloadAction } from "@reduxjs/toolkit";
import { AxiosError } from "axios";

import { defaultBilling } from "../consts/billing";
import {
  CLOSE_BILLING_URL,
  DELETE_BILLING_URL,
  GET_BILLINGS_LIST_URL,
  POST_BILLING_URL,
  PUT_BILLING_URL,
  SUBMIT_BILLING_URL,
} from "../middleware/routes";
import { RootState } from "../store";
import {
  BillingStep,
  IBillingState,
  UpdateBillingPaymentScheduleDate,
  UpdateBillingWeightedField,
} from "../types/billing";
import { IBilling, IBillingExpense } from "../types/billings";
import { Status } from "../types/state";
import { axiosRequestApi } from "../utils/axiosRequest";
import { createBillingExpenseFromPartial, getDefaultBillingStep } from "../utils/billing";
import {
  adaptBillingPutForBackend,
  adaptBillingResponseForFrontend,
  adaptBillingsErrorResponseForFrontend,
} from "./utils/billingsAdapt";

const initBillingsState: IBillingState = {
  requestId: "",
  status: Status.idle,
  isUpdating: false,
  errorMsg: null,
  billing: structuredClone(defaultBilling),
  originalBilling: structuredClone(defaultBilling),
  currentStep: BillingStep.Costs,
};

export const billingSlice = createSlice({
  name: "billing",
  initialState: initBillingsState,
  reducers: {
    updateBillingCurrentStep: (state: IBillingState, action: PayloadAction<BillingStep>) => {
      state.currentStep = action.payload;
    },
    updateBillingWeightedField: (state: IBillingState, action: PayloadAction<UpdateBillingWeightedField>) => {
      const { field, value } = action.payload;
      state.billing.weighted[field] = value;
    },
    updateBillingRealWasteAmount: (state: IBillingState, action: PayloadAction<IBilling["wasteAmount"]>) => {
      state.billing.wasteAmount = action.payload;
    },
    updateBillingExpectedWasteAmount: (
      state: IBillingState,
      action: PayloadAction<IBilling["expectedWasteAmount"]>
    ) => {
      state.billing.expectedWasteAmount = action.payload;
    },
    upsertBillingExpense: (state: IBillingState, action: PayloadAction<Partial<IBillingExpense>>) => {
      const id = action.payload.id;

      if (!id) {
        state.billing.expenses.push(
          createBillingExpenseFromPartial({
            ...action.payload,
          })
        );
        return;
      }
      state.billing.expenses = state.billing.expenses.map((expense) =>
        expense.id === action.payload.id ? { ...expense, ...action.payload } : expense
      );
    },
    removeBillingExpense: (state: IBillingState, action: PayloadAction<string>) => {
      const item = state.billing.expenses.find((expense) => expense.id === action.payload);
      if (item?.apiId) {
        state.billing.removeExpenseApiIds.push(item.apiId);
      }
      state.billing.expenses = state.billing.expenses.filter((expense) => expense.id !== action.payload);
    },
    updateBillingPaymentScheduleType: (
      state: IBillingState,
      action: PayloadAction<IBilling["paymentSchedule"]["type"]>
    ) => {
      state.billing.paymentSchedule.type = action.payload;
    },
    updateBillingPaymentDate: (state: IBillingState, action: PayloadAction<UpdateBillingPaymentScheduleDate>) => {
      const { field, value } = action.payload;
      state.billing.paymentSchedule[field] = value;
    },
    cleanUpBilling: (state: IBillingState) => {
      state.status = Status.success;
      state.errorMsg = null;
      state.billing = structuredClone(defaultBilling);
      state.currentStep = BillingStep.Costs;
    },
    resetBillingState: () => {
      return initBillingsState;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getBillingThunk.pending, (state, action) => {
        state.status = Status.requesting;
        state.requestId = action.meta.requestId;
      })
      .addCase(getBillingThunk.fulfilled, (state, action) => {
        if (state.requestId !== action.meta.requestId) {
          return;
        }

        const prevBillingId = state.originalBilling?.billingId;

        state.billing = structuredClone(action.payload);
        state.originalBilling = structuredClone(action.payload);

        if (prevBillingId !== action.payload.billingId) {
          state.currentStep = getDefaultBillingStep(action.payload.status, action.payload.hasBeenSaved);
        }

        state.errorMsg = null;
        state.status = Status.success;
      })
      .addCase(getBillingThunk.rejected, (state) => {
        state.status = Status.error;
        state.errorMsg = "there has been an error";
      })
      .addMatcher(
        isAnyOf(
          postCreateBillingThunk.pending,
          submitBillingThunk.pending,
          putUpdateBillingThunk.pending,
          closeBillingThunk.pending
        ),
        (state, action) => {
          state.status = Status.requesting;
          state.requestId = action.meta.requestId;
          state.isUpdating = true;
        }
      )
      .addMatcher(
        isAnyOf(
          postCreateBillingThunk.fulfilled,
          submitBillingThunk.fulfilled,
          putUpdateBillingThunk.fulfilled,
          closeBillingThunk.fulfilled
        ),
        (state, action) => {
          if (state.requestId !== action.meta.requestId) {
            return;
          }

          state.billing = structuredClone(action.payload);
          state.originalBilling = structuredClone(action.payload);

          state.errorMsg = null;
          state.status = Status.success;
          state.isUpdating = false;
        }
      )
      .addMatcher(
        isAnyOf(
          postCreateBillingThunk.rejected,
          submitBillingThunk.rejected,
          putUpdateBillingThunk.rejected,
          closeBillingThunk.rejected
        ),
        (state) => {
          state.status = Status.error;
          state.errorMsg = "there has been an error";
          state.isUpdating = false;
        }
      );
  },
});

export const getBillingThunk = createAsyncThunk<any, number, { state: RootState }>(
  "billing/getBillingThunk",
  async (id, { rejectWithValue }) => {
    try {
      const response = await axiosRequestApi.get(`${GET_BILLINGS_LIST_URL}/${id}`);
      return adaptBillingResponseForFrontend(response.data);
    } catch (error) {
      const axiosError = error as AxiosError<{ message?: string }>;
      const message = axiosError?.response?.data?.message;
      return rejectWithValue(adaptBillingsErrorResponseForFrontend(message));
    }
  }
);

export const postCreateBillingThunk = createAsyncThunk<any, { year: number }, { state: RootState }>(
  "billing/postCreateBillingThunk",
  async (params, { rejectWithValue }) => {
    try {
      const response = await axiosRequestApi.post(POST_BILLING_URL, {
        year: params.year,
      });

      return adaptBillingResponseForFrontend(response?.data);
    } catch (error) {
      const axiosError = error as AxiosError<{ message?: string }>;
      const message = axiosError?.response?.data?.message;
      return rejectWithValue(adaptBillingsErrorResponseForFrontend(message));
    }
  }
);

export const putUpdateBillingThunk = createAsyncThunk<any, void, { state: RootState }>(
  "billing/putUpdateBillingThunk",
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const data = state.billing.billing;
      const originalData = state.billing.originalBilling;

      const adaptedData = adaptBillingPutForBackend(data, originalData);

      if (!adaptedData) {
        return structuredClone(data);
      }

      const response = await axiosRequestApi.put(PUT_BILLING_URL, adaptedData);

      return adaptBillingResponseForFrontend(response?.data);
    } catch (error) {
      const axiosError = error as AxiosError<{ message?: string }>;
      const message = axiosError?.response?.data?.message;
      return rejectWithValue(adaptBillingsErrorResponseForFrontend(message));
    }
  }
);

export const submitBillingThunk = createAsyncThunk<any, void, { state: RootState }>(
  "billing/submitBillingThunk",
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const data = state.billing.billing;

      const response = await axiosRequestApi.post(`${SUBMIT_BILLING_URL}/${data.billingId}/submit`);

      return adaptBillingResponseForFrontend(response?.data);
    } catch (error) {
      const axiosError = error as AxiosError<{ message?: string }>;
      const message = axiosError?.response?.data?.message;
      return rejectWithValue(adaptBillingsErrorResponseForFrontend(message));
    }
  }
);

export const closeBillingThunk = createAsyncThunk<any, void, { state: RootState }>(
  "billing/closeBillingThunk",
  async (_, { rejectWithValue, getState }) => {
    try {
      const state = getState();
      const data = state.billing.billing;
      const response = await axiosRequestApi.post(`${CLOSE_BILLING_URL}/${data.billingId}/close`);

      return adaptBillingResponseForFrontend(response?.data);
    } catch (error) {
      const axiosError = error as AxiosError<{ message?: string }>;
      const message = axiosError?.response?.data?.message;
      return rejectWithValue(adaptBillingsErrorResponseForFrontend(message));
    }
  }
);

export const deleteBillingThunk = createAsyncThunk<any, number, { state: RootState }>(
  "billing/deleteBillingThunk",
  async (billingId, { rejectWithValue }) => {
    try {
      await axiosRequestApi.delete(`${DELETE_BILLING_URL}/${billingId}`);
      return;
    } catch (error) {
      const axiosError = error as AxiosError<{ message?: string }>;
      const message = axiosError?.response?.data?.message;
      return rejectWithValue(adaptBillingsErrorResponseForFrontend(message));
    }
  }
);

export const {
  updateBillingCurrentStep,
  updateBillingWeightedField,
  updateBillingExpectedWasteAmount,
  updateBillingRealWasteAmount,
  upsertBillingExpense,
  removeBillingExpense,
  updateBillingPaymentScheduleType,
  updateBillingPaymentDate,
  cleanUpBilling,
  resetBillingState,
} = billingSlice.actions;

export const billingReducer = billingSlice.reducer;
